import React, { useEffect } from 'react';
import { 
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  ScissorsIcon,
  CreditCardIcon,
  ClockIcon as DurationIcon
} from '@heroicons/react/24/outline';
import { useAppointmentValues } from '../../hooks/useAppointmentValues';

const AppointmentCard = ({ appointment, isMobile, showDetails = false }) => {
  const { 
    id, 
    date, 
    time, 
    clientName, 
    serviceName, 
    professionalName, 
    status, 
    duration,
    paymentMethod,
    isPaid,
    originalAppointment
  } = appointment;
  
  // Usar o hook de valores de agendamento
  const { formatAppointmentValue } = useAppointmentValues();
  
  // Adicionar logs para depuração
  useEffect(() => {
    console.log('[AppointmentCard] appointment recebido:', appointment);
    console.log('[AppointmentCard] originalAppointment:', originalAppointment);
  }, [appointment, originalAppointment]);

  const statusConfig = {
    completed: {
      icon: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
      text: 'Concluído',
      className: 'bg-green-50 text-green-700'
    },
    canceled: {
      icon: <XCircleIcon className="h-5 w-5 text-red-500" />,
      text: 'Cancelado',
      className: 'bg-red-50 text-red-700'
    },
    'no-show': {
      icon: <ClockIcon className="h-5 w-5 text-amber-500" />,
      text: 'Não compareceu',
      className: 'bg-amber-50 text-amber-700'
    }
  };

  const statusInfo = statusConfig[status] || statusConfig.completed;
  
  // Função auxiliar para obter o nome do profissional de forma mais robusta
  const getFormattedProfessionalName = () => {
    if (!professionalName) return 'Profissional não especificado';
    
    if (typeof professionalName === 'object') {
      return professionalName.nome || professionalName.name || 'Profissional não especificado';
    }
    
    return professionalName;
  };
  
  // Função auxiliar para formatar o método de pagamento
  const getFormattedPaymentMethod = () => {
    // Verificar se o pagamento está confirmado
    if (isPaid) {
      // Mapeamento de métodos de pagamento para exibição amigável
      const paymentMethodMap = {
        cash: 'em Dinheiro',
        credit: 'no Cartão de Crédito',
        debit: 'no Cartão de Débito',
        pix: 'via PIX',
        transfer: 'por Transferência',
        app: 'pelo Aplicativo',
        Confirmado: 'Confirmado'
      };
      
      // Se o método de pagamento não for informado ou for inválido, mostrar apenas "Pago"
      if (!paymentMethod || paymentMethod === 'Não informado' || paymentMethod === '') {
        return 'Pago';
      }
      
      const formattedMethod = paymentMethodMap[paymentMethod] || paymentMethod;
      
      // Se for "Confirmado", mostrar apenas "Pago"
      if (formattedMethod === 'Confirmado') {
        return 'Pago';
      }
      
      return `Pago ${formattedMethod}`;
    }
    
    // Se não está pago
    return 'Aguardando pagamento';
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${isMobile ? '' : 'hover:shadow-md transition-shadow'}`}>
      <div className="flex flex-col sm:flex-row">
        <div className="flex-1 p-4">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-gray-500 text-sm">
                {new Date(date).toLocaleDateString('pt-BR')} • {time}
              </span>
              <h3 className="font-medium text-gray-900">{serviceName}</h3>
              
              <div className="flex items-center mt-1 text-sm text-gray-600">
                <UserIcon className="h-4 w-4 mr-1" />
                <span>{clientName}</span>
              </div>
              
              <div className="flex items-center mt-1 text-sm text-gray-600">
                <ScissorsIcon className="h-4 w-4 mr-1" />
                <span>{getFormattedProfessionalName()}</span>
              </div>
            </div>
            
            <div className="flex flex-col items-end">
              <span className="font-bold text-gray-900">{formatAppointmentValue(originalAppointment || appointment)}</span>
              <span className={`px-2 py-1 rounded-full text-xs flex items-center mt-1 ${statusInfo.className}`}>
                {statusInfo.icon}
                <span className="ml-1">{statusInfo.text}</span>
              </span>
            </div>
          </div>
          
          {showDetails && (
            <div className="mt-3 pt-3 border-t text-sm">
              <div className="space-y-2">
                <div className="flex items-center">
                  <DurationIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-500">Duração:</span>
                  <span className="ml-2 font-medium">{duration} min</span>
                </div>
                
                <div className="flex items-center">
                  <CreditCardIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-500">Pagamento:</span>
                  <span className={`ml-2 ${isPaid ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
                    {getFormattedPaymentMethod()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentCard;
